import type { CustomCellEditorProps } from 'ag-grid-react';
import { get, uniq } from 'lodash-es';
import type { AgGridSearchSelectDropdownProps } from '../../AgGrid/types';
import { baseColumn } from './baseColumn';
import type { ColDefFactory, Column } from './types';

export interface ThresholdAssetColumnParams {
  limitAssetsBySymbol: ReadonlyMap<string, { Symbol: string }>;
  // Optional row-aware override for cases where valid threshold assets depend on row state.
  getLimitAssetsBySymbol?: (data: unknown) => ReadonlyMap<string, { Symbol: string }>;
  getLabel?: (asset: string) => string;
  getDescription?: (asset: string) => string | undefined;
  headerTooltip?: string;
  /**
   * If a Match Asset is selected, we sometimes want to also allow the user to select that asset as the threshold denominator.
   * Setting this option to the key of the Match Asset on the limit will read the current set Match Asset
   * and add it to the set of selectable Threshold Assets.
   */
  keyOfMatchAssetToIncludeInOptions?: string;
  shouldIncludeMatchAsset?: (data: unknown) => boolean;
}

/**
 * A column factory built for showing and setting threshold asset (currency or security) on limits
 */
export const thresholdAsset: ColDefFactory<Column<ThresholdAssetColumnParams>> = column => ({
  ...baseColumn(column),
  headerComponent: column.params?.headerTooltip ? 'tooltipHeader' : undefined,
  headerComponentParams: column.params?.headerTooltip ? { tooltip: column.params.headerTooltip } : undefined,
  cellRenderer: 'securityColumn',
  cellEditor: 'searchSelectDropdown',
  cellEditorPopup: true,
  suppressKeyboardEvent: params => params.event.key === 'Enter',
  cellEditorParams: (params: CustomCellEditorProps) => {
    const assetsBySymbol =
      column?.params?.getLimitAssetsBySymbol?.(params.data) ??
      column?.params?.limitAssetsBySymbol ??
      new Map<string, { Symbol: string }>();
    const options = [...assetsBySymbol.values()].map(asset => asset.Symbol);

    const shouldIncludeMatchAsset = column.params?.shouldIncludeMatchAsset?.(params.data) ?? true;

    if (shouldIncludeMatchAsset && column.params?.keyOfMatchAssetToIncludeInOptions) {
      const maybeMatchAsset: string | undefined = get(params.data, column.params.keyOfMatchAssetToIncludeInOptions);
      if (maybeMatchAsset) {
        options.push(maybeMatchAsset);
      }
    }

    return {
      ...params,
      useSearchSelectParams: {
        items: uniq(options),
        getLabel: item => column.params?.getLabel?.(item) ?? item,
        getDescription: item => column.params?.getDescription?.(item) ?? '',
      },
    } satisfies AgGridSearchSelectDropdownProps<string>;
  },
});
